import { type OAuthConnectionSummary } from "common";
import { type JSX, useState } from "react";
import { remoteStorageErrorMessage } from "./remote-storage.api.tsx";
import {
  useOAuthSharedDrives,
  useUpdateOAuthConnection,
} from "./remote-storage-oauth.api.tsx";

/** The save step of a Google Drive or Dropbox connection: its name and, for Drive, what to browse (data-2-1). */
export function OAuthConnectionForm({
  connection,
  onSaved,
  onCancel,
}: {
  connection: OAuthConnectionSummary;
  onSaved: (saved: OAuthConnectionSummary) => void;
  onCancel?: () => void;
}): JSX.Element {
  const isDrive = "drive" === connection.provider;
  const drives = useOAuthSharedDrives(connection.id, isDrive);
  const update = useUpdateOAuthConnection();
  const [name, setName] = useState(connection.name);
  const [sharedDriveId, setSharedDriveId] = useState(
    connection.sharedDriveId ?? ""
  );
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setError(null);
    try {
      onSaved(
        await update.mutateAsync({
          id: connection.id,
          body: { name: name.trim(), sharedDriveId: sharedDriveId || null },
        })
      );
    } catch (failure) {
      setError(remoteStorageErrorMessage(failure));
    }
  };

  return (
    <form
      className="space-y-4"
      onSubmit={event => {
        event.preventDefault();
        void save();
      }}>
      <p className="text-sm text-gray-600">
        Signed in as {connection.account}. Foundry can read these files but
        never change them.
      </p>
      <div>
        <label
          className="block text-sm font-medium leading-6 text-gray-900"
          htmlFor="oauth-name">
          Name
        </label>
        <input
          id="oauth-name"
          className="mt-2 block w-full rounded-md border-0 py-1.5 px-2 ring-1 ring-inset ring-gray-300 sm:text-sm"
          value={name}
          maxLength={128}
          onChange={event => setName(event.target.value)}
        />
      </div>
      {isDrive && (
        <div>
          <label
            className="block text-sm font-medium leading-6 text-gray-900"
            htmlFor="oauth-drive">
            Folder to browse
          </label>
          <select
            id="oauth-drive"
            className="mt-2 block w-full rounded-md border-0 py-1.5 px-2 ring-1 ring-inset ring-gray-300 sm:text-sm"
            value={sharedDriveId}
            onChange={event => setSharedDriveId(event.target.value)}>
            <option value="">My Drive</option>
            {(drives.data?.drives ?? []).map(drive => (
              <option key={drive.id} value={drive.id}>
                {`Shared drive: ${drive.name}`}
              </option>
            ))}
          </select>
          {drives.isError && (
            <p className="mt-1 text-xs text-rose-600">
              The shared drives of this account could not be listed. My Drive
              still works.
            </p>
          )}
        </div>
      )}
      {error && (
        <p className="text-sm text-rose-600" role="alert">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-3">
        {onCancel && (
          <button
            type="button"
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300"
            onClick={onCancel}>
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={update.isPending || 0 === name.trim().length}
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">
          Save
        </button>
      </div>
    </form>
  );
}
